import { valibotResolver } from '@hookform/resolvers/valibot'
import { useRouter } from '@tanstack/react-router'
import { type RcFile } from 'antd/es/upload'
import { useAtomValue } from 'jotai'
import { useCallback, useEffect, type FC } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { FaCheckCircle, FaDownload } from 'react-icons/fa'

import { Button, ModalBody, ModalFooter } from '@/components/elements'
import {
  ACTIONS,
  DESCRIPTIONS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES
} from '@/constants/common/message.constants'
import { toastApiErrorMessage } from '@/features/shared/utils'
import {
  csvTourImportJobs,
  downloadTourCsvTemplate
} from '@/features/tour-import-jobs/api'
import {
  tourImportFormValueSchema,
  type TourImportFormValue
} from '@/features/tour-import-jobs/models'
import { FileUploadDragger } from '@/features/uploads/components'
import { type UploadFileResult } from '@/features/uploads/models'
import { validateCsvFile } from '@/utils/upload-file-validator'

import { selectedDcIdAtom } from '../../-states'

type Props = {
  tenantKey: string
  onSuccess: () => Promise<void>
  onCancel: () => void
}

export const TourCsvImportModalContent: FC<Props> = ({
  tenantKey,
  onSuccess,
  onCancel
}) => {
  const router = useRouter()

  const {
    setValue,
    handleSubmit,
    formState: { isSubmitting, isValid }
  } = useForm<TourImportFormValue>({
    values: {
      fileKey: '',
      fileName: ''
    },
    resolver: valibotResolver(tourImportFormValueSchema)
  })

  const isValidUploadFile = useCallback((file: RcFile) => {
    return validateCsvFile(file)
  }, [])

  const handleChangeUploadFile = useCallback(
    (result: UploadFileResult | null) => {
      setValue('fileKey', result?.key ?? '', { shouldValidate: true })
      setValue('fileName', result?.name ?? '', { shouldValidate: true })
    },
    [setValue]
  )

  const selectedDcId = useAtomValue(selectedDcIdAtom)

  const handleDownloadTemplate = useCallback(async () => {
    try {
      await downloadTourCsvTemplate(tenantKey)
    } catch (error) {
      toastApiErrorMessage(error, ERROR_MESSAGES.FAILED_TO_DOWNLOAD)
    }
  }, [tenantKey])

  const onSubmit = useCallback(
    async (formValue: TourImportFormValue) => {
      try {
        await csvTourImportJobs({
          tenantKey,
          dto: { ...formValue, dcId: selectedDcId }
        })

        onSuccess()
        toast.success(SUCCESS_MESSAGES.STARTED_IMPORTING_TOUR_CSV)

        router.navigate({
          to: '/$tenantKey/tour-import-jobs',
          params: { tenantKey }
        })
      } catch (error) {
        toastApiErrorMessage(error, ERROR_MESSAGES.FAILED_TO_IMPORT_TOUR_CSV)
      }
    },
    [tenantKey, onSuccess, router, selectedDcId]
  )

  useEffect(() => {
    const unsubscribe = router.subscribe('onResolved', () => {
      onCancel()
    })
    return () => {
      unsubscribe()
    }
  }, [router, onCancel])

  return (
    <form
      onSubmit={handleSubmit(onSubmit, (error) => console.log('error', error))}
    >
      <ModalBody>
        <div className="mb-2 flex justify-end">
          <Button
            type="link"
            icon={<FaDownload />}
            onClick={handleDownloadTemplate}
          >
            {ACTIONS.DOWNLOAD_TEMPLATE}
          </Button>
        </div>
        <FileUploadDragger
          title={DESCRIPTIONS.UPLOAD_TOUR_CSV}
          description={
            <>
              {DESCRIPTIONS.CSV_FILE_UPLOAD}
              <br />
              {DESCRIPTIONS.PLEASE}
            </>
          }
          isValidFile={isValidUploadFile}
          onChangeUploadFile={handleChangeUploadFile}
        />
      </ModalBody>
      <ModalFooter className="!justify-start">
        <div className="flex w-full items-center gap-2"></div>
        <Button onClick={onCancel}>{ACTIONS.CANCEL}</Button>
        <Button
          type="primary"
          icon={<FaCheckCircle />}
          htmlType="submit"
          disabled={!isValid}
          loading={isSubmitting}
        >
          {ACTIONS.IMPORT}
        </Button>
      </ModalFooter>
    </form>
  )
}
